import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  getClientBusinesses,
  getPrimaryClientBusiness,
} from "../utils/clientBusinessAccess";
import {
  findBusinessBySelectionId,
  getBusinessSelectionId,
  getClientS3Root,
  getDocumentHomeFolder,
  withBusinessDocumentParams,
} from "../utils/businessDocumentContext";
import { normalizeDocumentCategory } from "../utils/moveDocumentScope";
import { CLIENT_ACTION } from "../redux/action";

function normalizeBusinessList(businesses) {
  const seen = new Set();
  return (businesses || []).filter((business) => {
    const name = String(business?.business_name || "").trim();
    if (!name) return false;
    const key = getBusinessSelectionId(business);
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

export function useBusinessDocumentScope({
  clientId,
  clientRecord,
  documentCategory,
} = {}) {
  const isBusinessCategory =
    normalizeDocumentCategory(documentCategory) === "business";

  const clientRecordRef = useRef(clientRecord);
  clientRecordRef.current = clientRecord;

  const legacyBusinesses = useMemo(
    () => getClientBusinesses(clientRecord),
    [clientRecord],
  );

  const [businesses, setBusinesses] = useState(legacyBusinesses);
  const [loadingBusinesses, setLoadingBusinesses] = useState(false);
  const fetchedClientIdRef = useRef(null);

  const refreshBusinesses = useCallback(async () => {
    if (!clientId) {
      const legacy = getClientBusinesses(clientRecordRef.current);
      setBusinesses(legacy);
      return legacy;
    }

    setLoadingBusinesses(true);
    try {
      const apiList = await CLIENT_ACTION.getClientBusinesses(clientId);
      if (Array.isArray(apiList) && apiList.length > 0) {
        const normalized = normalizeBusinessList(apiList);
        setBusinesses(normalized);
        return normalized;
      }
    } catch {
      /* fall back to client record */
    } finally {
      setLoadingBusinesses(false);
    }

    const legacy = getClientBusinesses(clientRecordRef.current);
    setBusinesses(legacy);
    return legacy;
  }, [clientId]);

  useEffect(() => {
    if (!isBusinessCategory) {
      setBusinesses([]);
      fetchedClientIdRef.current = null;
      return;
    }

    if (!clientId) {
      setBusinesses(legacyBusinesses);
      return;
    }

    if (fetchedClientIdRef.current !== clientId) {
      fetchedClientIdRef.current = clientId;
      refreshBusinesses();
      return;
    }

    // Client record updated (e.g. after add business) — refetch when local list is stale.
    const recordCount = legacyBusinesses.length;
    if (recordCount > businesses.length) {
      refreshBusinesses();
    }
  }, [
    isBusinessCategory,
    clientId,
    legacyBusinesses,
    businesses.length,
    refreshBusinesses,
  ]);

  const [selectedClientBusinessId, setSelectedClientBusinessId] =
    useState(null);

  const clientS3Root = useMemo(
    () => getClientS3Root(clientRecord),
    [clientRecord],
  );

  useEffect(() => {
    if (!isBusinessCategory) {
      setSelectedClientBusinessId(null);
      return;
    }
    if (!businesses.length) {
      setSelectedClientBusinessId(null);
      return;
    }
    setSelectedClientBusinessId((prev) => {
      if (prev && findBusinessBySelectionId(businesses, prev)) {
        return prev;
      }
      const primary = getPrimaryClientBusiness({ businesses });
      return (
        getBusinessSelectionId(primary) ||
        getBusinessSelectionId(businesses[0]) ||
        null
      );
    });
  }, [isBusinessCategory, businesses]);

  const selectedBusiness = useMemo(
    () => findBusinessBySelectionId(businesses, selectedClientBusinessId),
    [businesses, selectedClientBusinessId],
  );

  const documentHomeFolder = useMemo(
    () =>
      getDocumentHomeFolder({
        clientData: clientRecord,
        documentCategory,
        selectedBusiness,
      }),
    [clientRecord, documentCategory, selectedBusiness],
  );

  const appendDocScope = useCallback(
    (payload = {}) => {
      const scoped = withBusinessDocumentParams(payload, {
        documentCategory,
        clientBusinessId: selectedClientBusinessId,
      });
      if (isBusinessCategory && documentHomeFolder) {
        scoped.home_folder = documentHomeFolder;
      } else if (clientS3Root) {
        scoped.home_folder = clientS3Root;
      }
      return scoped;
    },
    [
      documentCategory,
      selectedClientBusinessId,
      isBusinessCategory,
      documentHomeFolder,
      clientS3Root,
    ],
  );

  return {
    isBusinessCategory,
    businesses,
    loadingBusinesses,
    refreshBusinesses,
    selectedClientBusinessId,
    setSelectedClientBusinessId,
    selectedBusiness,
    clientS3Root,
    documentHomeFolder,
    /** @deprecated use documentHomeFolder — kept for gradual migration */
    homeFolder: documentHomeFolder,
    appendDocScope,
    showBusinessSelector: isBusinessCategory && businesses.length > 0,
  };
}
